import React, { createContext, useContext, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

const AlertContext = createContext(null);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  animation: ${fadeIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const Dialog = styled.div`
  position: relative;
  width: min(480px, 92vw);
  border-radius: 32px;
  padding: 32px;
  
  /* Glassy Background */
  background: rgba(30, 30, 40, 0.65);
  box-shadow: 
    0 40px 100px -20px rgba(0, 0, 0, 0.7),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);
    
  backdrop-filter: blur(40px) saturate(150%);
  -webkit-backdrop-filter: blur(40px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.08);

  color: #f5f5f7;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${scaleIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;

  /* Subtle inner glow */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 32px;
    padding: 1px;
    background: linear-gradient(145deg, rgba(255,255,255,0.15), transparent 60%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;

  h3 {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #fff 0%, #e0e0e0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  p {
    margin: 0;
    color: rgba(235, 235, 245, 0.7);
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 14px 18px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
  color: white;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);

  &::placeholder {
    color: rgba(235, 235, 245, 0.3);
  }

  &:focus {
    background: rgba(0, 0, 0, 0.3);
    border-color: rgba(10, 132, 255, 0.5);
    box-shadow: 
      inset 0 2px 4px rgba(0,0,0,0.2),
      0 0 0 4px rgba(10, 132, 255, 0.15);
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: stretch;
  gap: 12px;
  width: 100%;

  @media (min-width: 400px) {
    flex-direction: row;
  }
`;

const Button = styled.button`
  flex: 1;
  border: none;
  border-radius: 16px;
  padding: 14px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;

  ${props => props.variant === 'primary' ? css`
    background: linear-gradient(135deg, #0a84ff 0%, #0077ed 100%);
    color: white;
    box-shadow: 
      0 4px 12px rgba(10, 132, 255, 0.3),
      inset 0 1px 0 rgba(255,255,255,0.2);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 
        0 6px 16px rgba(10, 132, 255, 0.4),
        inset 0 1px 0 rgba(255,255,255,0.2);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 
        0 2px 8px rgba(10, 132, 255, 0.3),
        inset 0 1px 0 rgba(255,255,255,0.2);
    }
  ` : css`
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.05);
    
    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    &:active {
      background: rgba(255, 255, 255, 0.06);
    }
  `}
`;

export const AlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState(null);

  const showAlert = ({ title = 'Heads up', message, primaryLabel = 'Got it', secondaryLabel, onPrimary, onSecondary }) => {
    setAlertState({
      title,
      message,
      primaryLabel,
      secondaryLabel,
      onPrimary,
      onSecondary,
      type: 'alert'
    });
  };

  const showPrompt = ({ title, message, placeholder = '', defaultValue = '', confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm, onCancel }) => {
    setAlertState({
      title,
      message,
      primaryLabel: confirmLabel,
      secondaryLabel: cancelLabel,
      placeholder,
      inputValue: defaultValue,
      type: 'prompt',
      onPrimary: (value) => onConfirm && onConfirm(value),
      onSecondary: onCancel
    });
  };

  const close = () => setAlertState(null);

  const handlePrimary = () => {
    if (alertState?.type === 'prompt') {
      alertState.onPrimary?.(alertState.inputValue);
    } else {
      alertState?.onPrimary?.();
    }
    close();
  };

  const handleSecondary = () => {
    alertState?.onSecondary?.();
    close();
  };

  return (
    <AlertContext.Provider value={{ showAlert, showPrompt }}>
      {children}
      {alertState && (
        <Overlay onClick={close}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <Header>
              <h3>{alertState.title}</h3>
              {alertState.message && <p>{alertState.message}</p>}
            </Header>

            {alertState.type === 'prompt' && (
              <InputContainer>
                <StyledInput
                  autoFocus
                  type="text"
                  value={alertState.inputValue}
                  onChange={(e) => setAlertState(prev => ({ ...prev, inputValue: e.target.value }))}
                  placeholder={alertState.placeholder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handlePrimary();
                  }}
                />
              </InputContainer>
            )}

            <Actions>
              {alertState.secondaryLabel && (
                <Button type="button" variant="ghost" onClick={handleSecondary}>
                  {alertState.secondaryLabel}
                </Button>
              )}
              <Button type="button" variant="primary" onClick={handlePrimary}>
                {alertState.primaryLabel}
              </Button>
            </Actions>
          </Dialog>
        </Overlay>
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => useContext(AlertContext);
